<template>
  <HrPageLayout
    title="Department Management"
    subtitle="Manage departments and organization structure"
    icon="🏢"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/dashboard' },
      { label: 'Departments' }
    ]"
  >
    <template #header-actions>
      <button class="hr-btn hr-btn-primary" @click="openAddModal">
        <i class="bx bx-plus"></i> Add Department
      </button>
      <button class="hr-btn hr-btn-success" @click="downloadReport">
        <i class="bx bxs-download"></i> Download
      </button>
    </template>

    <div class="department-management-content">
      <!-- Stats cards -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-building"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Departments</p>
          <h2 class="hr-metric-value">{{ stats.totalDepartments }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Active Departments</p>
          <h2 class="hr-metric-value">{{ stats.activeDepartments }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-group"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Employees</p>
          <h2 class="hr-metric-value">{{ stats.totalEmployees }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-dollar"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Average Budget</p>
          <h2 class="hr-metric-value">{{ formatCurrency(stats.averageBudget) }}</h2>
            </div>
        </div>
      </div>

      <!-- Search and filters -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body hr-py-3">
          <div class="hr-filters-row">
            <div class="hr-search-wrap">
              <input v-model="search" @input="filterDepartments" class="hr-input"
                     placeholder="Search departments..." spellcheck="false" type="text">
              <i class="bx bx-search-alt search-icon"></i>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Location</label>
              <select v-model="selectedLocation" @change="filterDepartments" class="hr-select" style="min-width: 200px;">
                <option value="">All Locations</option>
                <option v-for="location in locations" :key="location" :value="location">{{ location }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Department data table -->
      <div class="hr-chart-card">
        <div class="hr-chart-body">
          <div class="hr-table-wrapper">
            <table class="hr-table hr-table-hover hr-table-striped">
              <thead>
                <tr>
                  <th>Department Name</th>
                  <th>Location</th>
                  <th>Manager</th>
                  <th>Employees</th>
                  <th>Budget</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="7" class="hr-text-center hr-py-4">
                    <div class="hr-spinner"></div>
                  </td>
                </tr>
                <tr v-else-if="filteredDepartments.length === 0">
                  <td colspan="7" class="hr-text-center hr-py-4 hr-text-muted">
                    No departments found matching your criteria
                  </td>
                </tr>
                <tr v-for="department in filteredDepartments" :key="department.id">
                  <td>
                    <div class="dept-cell-name">
                      <div class="dept-avatar">{{ department.departmentName.charAt(0).toUpperCase() }}</div>
                      <div>
                        <strong>{{ department.departmentName }}</strong>
                        <p class="hr-text-muted hr-mb-0 hr-small">{{ department.description || 'No description' }}</p>
                      </div>
                    </div>
                  </td>
                  <td>{{ department.location || 'Not specified' }}</td>
                  <td>
                    <div v-if="department.manager">
                      <strong>{{ department.manager }}</strong>
                      <p class="hr-text-muted hr-mb-0 hr-small"><i class="bx bx-envelope"></i> {{ department.managerEmail || '' }}</p>
                    </div>
                    <span v-else class="hr-text-muted">No manager assigned</span>
                  </td>
                  <td><span class="hr-badge hr-badge-info">{{ department.employeeCount || 0 }}</span></td>
                  <td>{{ formatCurrency(department.budget) }}</td>
                  <td>
                    <span :class="'hr-badge ' + getStatusBadgeClass(department.status)">{{ department.status || 'Active' }}</span>
                  </td>
                  <td>
                    <div class="hr-btn-group">
                      <button class="hr-btn hr-btn-sm hr-btn-primary" @click="viewDepartment(department)">
                        <i class="bx bx-show"></i>
                      </button>
                      <button class="hr-btn hr-btn-sm hr-btn-warning" @click="editDepartment(department)">
                        <i class="bx bx-edit"></i>
                      </button>
                      <button class="hr-btn hr-btn-sm hr-btn-danger" @click="confirmDelete(department)">
                        <i class="bx bx-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="pagination.totalPages > 1" class="hr-pagination-wrap hr-mt-4">
            <div class="hr-pagination-info">
              Showing {{ ((pagination.currentPage - 1) * pagination.limit) + 1 }} to
              {{ Math.min(pagination.currentPage * pagination.limit, pagination.totalCount) }}
              of {{ pagination.totalCount }} entries
            </div>
            <nav class="hr-pagination">
              <button type="button" class="hr-page-btn" :disabled="pagination.currentPage === 1"
                      @click="changePage(pagination.currentPage - 1)">Previous</button>
              <template v-for="page in getVisiblePages()" :key="page">
                <button type="button" class="hr-page-btn"
                        :class="{ 'hr-page-active': page === pagination.currentPage }"
                        @click="changePage(page)">{{ page }}</button>
              </template>
              <button type="button" class="hr-page-btn"
                      :disabled="pagination.currentPage === pagination.totalPages"
                      @click="changePage(pagination.currentPage + 1)">Next</button>
            </nav>
          </div>
        </div>
      </div>

      <!-- Add Department Modal -->
      <ModalWrapper ref="addDepartmentModal" title="Add New Department" size="large">
        <template #body>
          <form @submit.prevent="addDepartment" class="hr-form-grid">
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Department Name <span class="hr-text-danger">*</span></label>
              <input v-model="newDepartment.departmentName" class="hr-input" placeholder="Enter department name" required type="text">
            </div>
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Location</label>
              <input v-model="newDepartment.location" class="hr-input" placeholder="Enter location" type="text">
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Manager</label>
              <select v-model="newDepartment.managerId" @change="onManagerChange('new')" class="hr-select">
                <option value="">Select Manager (Optional)</option>
                <option v-for="manager in availableManagers" :key="manager.id" :value="manager.id">
                  {{ manager.firstName }} {{ manager.lastName }}
                  <span v-if="manager.designation"> - {{ manager.designation }}</span>
                  <span v-if="manager.departmentName"> ({{ manager.departmentName }})</span>
                </option>
              </select>
              <small class="hr-text-muted hr-d-block hr-mt-1">
                <span v-if="loadingManagers" class="hr-text-info"><i class="bx bx-loader-alt bx-spin"></i> Loading managers...</span>
                <span v-else-if="selectedManagerInfo.new">
                  Selected: {{ selectedManagerInfo.new.firstName }} {{ selectedManagerInfo.new.lastName }}
                  <br>Email: {{ selectedManagerInfo.new.email || 'Not available' }}
                  <br>Phone: {{ selectedManagerInfo.new.phone || 'Not available' }}
                </span>
              </small>
            </div>
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Budget</label>
              <div class="hr-budget-input">
                <select v-model="newDepartment.currency" class="hr-select" style="max-width: 90px;">
                  <option value="KES">KES</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
                <input v-model="newDepartment.budget" class="hr-input" placeholder="Amount" type="number" step="0.01">
              </div>
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Description</label>
              <textarea v-model="newDepartment.description" class="hr-textarea" placeholder="Enter department description" rows="3"></textarea>
            </div>
          </form>
        </template>
        <template #footer>
          <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.addDepartmentModal?.close()">Cancel</button>
          <button type="button" class="hr-btn hr-btn-primary" @click="addDepartment" :disabled="addingDepartment">
            <span v-if="addingDepartment" class="hr-spinner small"></span>
            <i v-else class="bx bx-check"></i> Add Department
          </button>
        </template>
      </ModalWrapper>

      <!-- Edit Department Modal -->
      <ModalWrapper ref="editDepartmentModal" title="Edit Department" size="large">
        <template #body>
          <form v-if="editingDepartment" @submit.prevent="updateDepartment" class="hr-form-grid">
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Department Name <span class="hr-text-danger">*</span></label>
              <input v-model="editingDepartment.departmentName" class="hr-input" required type="text">
            </div>
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Location</label>
              <input v-model="editingDepartment.location" class="hr-input" type="text">
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Manager</label>
              <select v-model="editingDepartment.managerId" @change="onManagerChange('edit')" class="hr-select">
                <option value="">Select Manager (Optional)</option>
                <option v-for="manager in availableManagers" :key="manager.id" :value="manager.id">
                  {{ manager.firstName }} {{ manager.lastName }}
                  <span v-if="manager.designation"> - {{ manager.designation }}</span>
                  <span v-if="manager.departmentName"> ({{ manager.departmentName }})</span>
                </option>
              </select>
              <small v-if="selectedManagerInfo.edit" class="hr-text-muted hr-d-block hr-mt-1">
                Selected: {{ selectedManagerInfo.edit.firstName }} {{ selectedManagerInfo.edit.lastName }}
                <br>Email: {{ selectedManagerInfo.edit.email || 'Not available' }}
                <br>Phone: {{ selectedManagerInfo.edit.phone || 'Not available' }}
              </small>
            </div>
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Budget</label>
              <div class="hr-budget-input">
                <select v-model="editingDepartment.currency" class="hr-select" style="max-width: 90px;">
                  <option value="KES">KES</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
                <input v-model="editingDepartment.budget" class="hr-input" type="number" step="0.01">
              </div>
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Description</label>
              <textarea v-model="editingDepartment.description" class="hr-textarea" rows="3"></textarea>
            </div>
          </form>
        </template>
        <template #footer>
          <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.editDepartmentModal?.close()">Cancel</button>
          <button type="button" class="hr-btn hr-btn-warning" @click="updateDepartment" :disabled="updatingDepartment">
            <span v-if="updatingDepartment" class="hr-spinner small"></span>
            <i v-else class="bx bx-check"></i> Update Department
          </button>
        </template>
      </ModalWrapper>

      <!-- View Department Modal -->
      <ModalWrapper ref="viewDepartmentModal" title="Department Details" size="large">
        <template #body v-if="selectedDepartment">
          <div class="dept-view-grid">
            <div class="hr-chart-card">
              <div class="hr-chart-header"><h6 class="hr-chart-title hr-mb-0">Basic Information</h6></div>
              <div class="hr-chart-body">
                <div class="dept-view-item"><label class="hr-text-muted">Department Name:</label><p class="hr-mb-0"><strong>{{ selectedDepartment.departmentName }}</strong></p></div>
                <div class="dept-view-item"><label class="hr-text-muted">Location:</label><p class="hr-mb-0">{{ selectedDepartment.location || 'Not specified' }}</p></div>
                <div class="dept-view-item"><label class="hr-text-muted">Budget:</label><p class="hr-mb-0">{{ formatCurrency(selectedDepartment.budget) }}</p></div>
                <div class="dept-view-item"><label class="hr-text-muted">Created:</label><p class="hr-mb-0">{{ formatDate(selectedDepartment.createdAt) }}</p></div>
                <div class="dept-view-item"><label class="hr-text-muted">Last Updated:</label><p class="hr-mb-0">{{ formatDate(selectedDepartment.updatedAt) }}</p></div>
              </div>
            </div>
            <div class="hr-chart-card">
              <div class="hr-chart-header"><h6 class="hr-chart-title hr-mb-0">Manager Information</h6></div>
              <div class="hr-chart-body">
                <div class="dept-view-item"><label class="hr-text-muted">Manager:</label><p class="hr-mb-0">{{ selectedDepartment.manager || 'No manager assigned' }}</p></div>
                <div class="dept-view-item"><label class="hr-text-muted">Email:</label><p class="hr-mb-0">{{ selectedDepartment.managerEmail || 'Not provided' }}</p></div>
                <div class="dept-view-item"><label class="hr-text-muted">Phone:</label><p class="hr-mb-0">{{ selectedDepartment.managerPhone || 'Not provided' }}</p></div>
                <div class="dept-view-item"><label class="hr-text-muted">Employees:</label><p class="hr-mb-0"><span class="hr-badge hr-badge-info">{{ selectedDepartment.employeeCount || 0 }}</span></p></div>
              </div>
            </div>
          </div>
          <div class="hr-chart-card hr-mt-3">
            <div class="hr-chart-header"><h6 class="hr-chart-title hr-mb-0">Description</h6></div>
            <div class="hr-chart-body"><p class="hr-mb-0">{{ selectedDepartment.description || 'No description provided' }}</p></div>
          </div>
          <div v-if="departmentEmployees.length > 0" class="hr-chart-card hr-mt-3">
            <div class="hr-chart-header hr-flex-between">
              <h6 class="hr-chart-title hr-mb-0">Department Employees</h6>
              <span class="hr-badge hr-badge-primary">{{ departmentEmployees.length }}</span>
            </div>
            <div class="hr-chart-body hr-p-0">
              <div class="hr-table-wrapper">
                <table class="hr-table">
                  <thead><tr><th>Employee ID</th><th>Name</th><th>Position</th><th>Email</th><th>Status</th></tr></thead>
                  <tbody>
                    <tr v-for="employee in departmentEmployees" :key="employee.id">
                      <td>{{ employee.empNo || employee.nationalId }}</td>
                      <td>{{ employee.firstName }} {{ employee.lastName }}</td>
                      <td>{{ employee.title || employee.designation || 'N/A' }}</td>
                      <td>{{ employee.email }}</td>
                      <td><span :class="'hr-badge ' + getEmployeeStatusBadge(employee.status)">{{ employee.status }}</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>
        <template #footer>
          <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.viewDepartmentModal?.close()">Close</button>
          <button type="button" class="hr-btn hr-btn-warning" @click="openEditFromView">
            <i class="bx bx-edit"></i> Edit Department
          </button>
        </template>
      </ModalWrapper>

      <!-- Delete Confirmation Modal -->
      <ModalWrapper ref="deleteConfirmModal" title="Confirm Delete">
        <template #body>
          <div class="hr-text-center">
            <div class="hr-mb-3"><i class="bx bx-error-circle hr-text-danger" style="font-size: 3rem;"></i></div>
            <h5>Are you sure?</h5>
            <p class="hr-mb-0">
              You are about to delete <strong>{{ departmentToDelete?.departmentName }}</strong>.
              This action cannot be undone.
            </p>
            <div v-if="departmentToDelete?.employeeCount > 0" class="hr-alert hr-alert-warning hr-mt-3">
              <strong>Warning:</strong> This department has {{ departmentToDelete.employeeCount }} employees.
              Please reassign them before deleting.
            </div>
          </div>
        </template>
        <template #footer>
          <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.deleteConfirmModal?.close()">Cancel</button>
          <button type="button" class="hr-btn hr-btn-danger" @click="deleteDepartment" :disabled="deletingDepartment">
            <span v-if="deletingDepartment" class="hr-spinner small"></span>
            <i v-else class="bx bx-trash"></i> Delete Department
          </button>
        </template>
      </ModalWrapper>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { execute, authUrl } from "@/api";
import moment from "moment/moment";
import * as XLSX from 'xlsx';

export default {
  name: "DepartmentManagement",
  components: {
    HrPageLayout,
    ModalWrapper
  },
  data() {
    return {
      departments: [],
      filteredDepartments: [],
      locations: [],
      selectedLocation: "",
      search: "",
      selectedDepartment: null,
      departmentEmployees: [],
      departmentToDelete: null,
      editingDepartment: null,
      availableManagers: [],
      selectedManagerInfo: {
        new: null,
        edit: null
      },
      newDepartment: {
        departmentName: '',
        location: '',
        managerId: '',
        budget: '',
        currency: 'KES',
        description: ''
      },
      stats: {
        totalDepartments: 0,
        activeDepartments: 0,
        totalEmployees: 0,
        averageBudget: 0
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10
      },
      loading: false,
      loadingManagers: false,
      addingDepartment: false,
      updatingDepartment: false,
      deletingDepartment: false,
      loadingEmployees: false,
      error: null
    };
  },
  mounted() {
    this.fetchDepartments();
    this.fetchAvailableManagers();
  },
  methods: {
    fetchDepartments() {
      this.loading = true;
      this.departments = [];
      this.filteredDepartments = [];

      const data = new FormData();
      data.append("function", "getDepartmentsWithDetails");
      data.append("page", this.pagination.currentPage);
      data.append("limit", this.pagination.limit);

      if (this.search) {
        data.append("search", this.search);
      }

      if (this.selectedLocation) {
        data.append("location", this.selectedLocation);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.departments = Array.isArray(res.data.data.departments) ? res.data.data.departments : [];
              this.stats = res.data.data.stats || {
                totalDepartments: 0,
                activeDepartments: 0,
                totalEmployees: 0,
                averageBudget: 0
              };
              this.locations = Array.isArray(res.data.data.locations) ? res.data.data.locations : [];
              this.pagination = res.data.data.pagination || this.pagination;
              this.filterDepartments();
            } else {
              this.error = res.data.message || "Failed to fetch departments";
              this.departments = [];
              this.filteredDepartments = [];
            }
          })
          .catch((e) => {
            this.loading = false;
            this.error = e.message || "An error occurred while fetching departments";
            console.error("Error fetching departments:", e);
            this.departments = [];
            this.filteredDepartments = [];
          });
    },

    fetchAvailableManagers() {
      this.loadingManagers = true;
      this.availableManagers = [];

      const data = new FormData();
      data.append("function", "getPotentialManagers");

      execute(data, authUrl)
          .then((res) => {
            this.loadingManagers = false;
            if (res.data.success) {
              this.availableManagers = Array.isArray(res.data.data.managers) ? res.data.data.managers : [];
            } else {
              this.error = res.data.message || "Failed to fetch potential managers";
              this.availableManagers = [];
            }
          })
          .catch((e) => {
            this.loadingManagers = false;
            this.error = e.message || "An error occurred while fetching managers";
            console.error("Error fetching managers:", e);
            this.availableManagers = [];
          });
    },

    onManagerChange(type) {
      const managerId = type === 'new' ? this.newDepartment.managerId : this.editingDepartment.managerId;

      if (!managerId) {
        this.selectedManagerInfo[type] = null;
        return;
      }

      // Find the selected manager from the available managers list
      const selectedManager = this.availableManagers.find(manager => manager.id == managerId);
      if (selectedManager) {
        this.selectedManagerInfo[type] = selectedManager;
      } else {
        // If not found in the list, fetch from API
        this.getManagerDetails(managerId, type);
      }
    },

    getManagerDetails(managerId, type) {
      const data = new FormData();
      data.append("function", "getManagerDetails");
      data.append("managerId", managerId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.selectedManagerInfo[type] = res.data.data.manager;
            } else {
              this.selectedManagerInfo[type] = null;
              console.error("Failed to fetch manager details:", res.data.message);
            }
          })
          .catch((e) => {
            this.selectedManagerInfo[type] = null;
            console.error("Error fetching manager details:", e);
          });
    },

    openAddModal() {
      this.newDepartment = {
        departmentName: '',
        location: '',
        managerId: '',
        budget: '',
        currency: 'KES',
        description: ''
      };
      this.selectedManagerInfo.new = null;
      if (this.availableManagers.length === 0) this.fetchAvailableManagers();
      this.$nextTick(() => this.$refs.addDepartmentModal?.open());
    },

    openEditFromView() {
      this.$refs.viewDepartmentModal?.close();
      this.editDepartment(this.selectedDepartment);
    },

    filterDepartments() {
      if (!Array.isArray(this.departments)) {
        this.filteredDepartments = [];
        return;
      }

      let filtered = [...this.departments];

      // Filter by search term
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        filtered = filtered.filter(dept =>
            (dept.departmentName && dept.departmentName.toLowerCase().includes(searchLower)) ||
            (dept.manager && dept.manager.toLowerCase().includes(searchLower)) ||
            (dept.location && dept.location.toLowerCase().includes(searchLower))
        );
      }

      this.filteredDepartments = filtered;
    },

    viewDepartment(department) {
      this.selectedDepartment = { ...department };
      this.getDepartmentEmployees(department.id);
      this.$nextTick(() => this.$refs.viewDepartmentModal?.open());
    },

    getDepartmentEmployees(departmentId) {
      this.loadingEmployees = true;
      this.departmentEmployees = [];

      const data = new FormData();
      data.append("function", "getDepartmentEmployees");
      data.append("departmentId", departmentId);

      execute(data, authUrl)
          .then((res) => {
            this.loadingEmployees = false;
            if (res.data.success) {
              this.departmentEmployees = Array.isArray(res.data.data.employees) ? res.data.data.employees : [];
            } else {
              this.error = res.data.message || "Failed to fetch department employees";
              this.departmentEmployees = [];
            }
          })
          .catch((e) => {
            this.loadingEmployees = false;
            this.error = e.message || "An error occurred while fetching employees";
            console.error("Error fetching employees:", e);
            this.departmentEmployees = [];
          });
    },

    editDepartment(department) {
      this.editingDepartment = { ...department };
      this.selectedManagerInfo.edit = null;
      if (department.manager) {
        const manager = this.availableManagers.find(m =>
            `${m.firstName} ${m.lastName}` === department.manager
        );
        if (manager) {
          this.editingDepartment.managerId = manager.id;
          this.selectedManagerInfo.edit = manager;
        }
      }
      this.$nextTick(() => this.$refs.editDepartmentModal?.open());
    },

    addDepartment() {
      if (!this.newDepartment.departmentName.trim()) {
        alert("Department name is required");
        return;
      }

      this.addingDepartment = true;

      const data = new FormData();
      data.append("function", "addDepartmentWithManager");
      Object.keys(this.newDepartment).forEach(key => {
        data.append(key, this.newDepartment[key]);
      });

      execute(data, authUrl)
          .then((res) => {
            this.addingDepartment = false;
            if (res.data.success) {
              this.$refs.addDepartmentModal?.close();
              // Reset form
              this.newDepartment = {
                departmentName: '',
                location: '',
                managerId: '',
                budget: '',
                currency: 'KES',
                description: ''
              };
              this.selectedManagerInfo.new = null;

              // Refresh departments
              this.fetchDepartments();

              // Show success message
              this.showSuccessMessage("Department added successfully");
            } else {
              this.error = res.data.message || "Failed to add department";
              alert(this.error);
            }
          })
          .catch((e) => {
            this.addingDepartment = false;
            this.error = e.message || "An error occurred while adding department";
            console.error("Error adding department:", e);
            alert(this.error);
          });
    },

    updateDepartment() {
      if (!this.editingDepartment.departmentName.trim()) {
        alert("Department name is required");
        return;
      }

      this.updatingDepartment = true;

      const data = new FormData();
      data.append("function", "updateDepartmentWithManager");
      Object.keys(this.editingDepartment).forEach(key => {
        data.append(key, this.editingDepartment[key]);
      });

      execute(data, authUrl)
          .then((res) => {
            this.updatingDepartment = false;
            if (res.data.success) {
              this.$refs.editDepartmentModal?.close();
              // Refresh departments
              this.fetchDepartments();

              // Show success message
              this.showSuccessMessage("Department updated successfully");

              this.editingDepartment = null;
              this.selectedManagerInfo.edit = null;
            } else {
              this.error = res.data.message || "Failed to update department";
              alert(this.error);
            }
          })
          .catch((e) => {
            this.updatingDepartment = false;
            this.error = e.message || "An error occurred while updating department";
            console.error("Error updating department:", e);
            alert(this.error);
          });
    },

    confirmDelete(department) {
      this.departmentToDelete = department;
      this.$nextTick(() => this.$refs.deleteConfirmModal?.open());
    },

    deleteDepartment() {
      if (!this.departmentToDelete) return;

      this.deletingDepartment = true;

      const data = new FormData();
      data.append("function", "deleteDepartment");
      data.append("departmentId", this.departmentToDelete.id);

      execute(data, authUrl)
          .then((res) => {
            this.deletingDepartment = false;
            if (res.data.success) {
              this.$refs.deleteConfirmModal?.close();
              // Refresh departments
              this.fetchDepartments();

              // Show success message
              this.showSuccessMessage("Department deleted successfully");

              this.departmentToDelete = null;
            } else {
              this.error = res.data.message || "Failed to delete department";
              alert(this.error);
            }
          })
          .catch((e) => {
            this.deletingDepartment = false;
            this.error = e.message || "An error occurred while deleting department";
            console.error("Error deleting department:", e);
            alert(this.error);
          });
    },

    changePage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.pagination.currentPage = page;
        this.fetchDepartments();
      }
    },

    getVisiblePages() {
      const current = this.pagination.currentPage;
      const total = this.pagination.totalPages;
      const delta = 2;
      const range = [];
      const rangeWithDots = [];

      for (let i = Math.max(2, current - delta);
           i <= Math.min(total - 1, current + delta);
           i++) {
        range.push(i);
      }

      if (current - delta > 2) {
        rangeWithDots.push(1, '...');
      } else {
        rangeWithDots.push(1);
      }

      rangeWithDots.push(...range);

      if (current + delta < total - 1) {
        rangeWithDots.push('...', total);
      } else {
        rangeWithDots.push(total);
      }

      return rangeWithDots.filter((page, index, arr) => arr.indexOf(page) === index);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return moment(dateString).format('DD MMM YYYY');
    },

    formatCurrency(amount) {
      if (!amount || isNaN(amount)) return 'N/A';
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES'
      }).format(amount);
    },

    getStatusBadgeClass(status) {
      switch (status?.toLowerCase()) {
        case 'active': return 'hr-badge-success';
        case 'inactive': return 'hr-badge-secondary';
        case 'suspended': return 'hr-badge-warning';
        default: return 'hr-badge-success';
      }
    },

    getEmployeeStatusBadge(status) {
      switch (status?.toLowerCase()) {
        case 'active': return 'hr-badge-success';
        case 'inactive': return 'hr-badge-secondary';
        case 'suspended': return 'hr-badge-warning';
        case 'terminated': return 'hr-badge-danger';
        default: return 'hr-badge-primary';
      }
    },

    showSuccessMessage(message) {
      // You can implement a toast notification here
      alert(message);
    },

    downloadReport() {
      try {
        if (!Array.isArray(this.departments) || this.departments.length === 0) {
          alert("No data available for download.");
          return;
        }

        // Create worksheet data
        const worksheetData = [];

        // Add headers
        worksheetData.push([
          'Department ID',
          'Department Name',
          'Location',
          'Manager',
          'Manager Email',
          'Manager Phone',
          'Budget',
          'Currency',
          'Employee Count',
          'Status',
          'Created Date',
          'Description'
        ]);

        // Add data rows
        this.filteredDepartments.forEach(department => {
          worksheetData.push([
            department.id || '',
            department.departmentName || '',
            department.location || '',
            department.manager || '',
            department.managerEmail || '',
            department.managerPhone || '',
            department.budget || '',
            department.currency || 'KES',
            department.employeeCount || 0,
            department.status || 'Active',
            this.formatDate(department.createdAt),
            department.description || ''
          ]);
        });

        // Create worksheet
        const ws = XLSX.utils.aoa_to_sheet(worksheetData);

        // Set column widths
        const wscols = [
          { wch: 10 }, // Department ID
          { wch: 25 }, // Department Name
          { wch: 20 }, // Location
          { wch: 20 }, // Manager
          { wch: 25 }, // Manager Email
          { wch: 15 }, // Manager Phone
          { wch: 15 }, // Budget
          { wch: 10 }, // Currency
          { wch: 12 }, // Employee Count
          { wch: 10 }, // Status
          { wch: 15 }, // Created Date
          { wch: 30 }  // Description
        ];
        ws['!cols'] = wscols;

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Departments");

        // Generate filename
        const fileName = `Departments_Report_${moment().format('YYYY-MM-DD')}.xlsx`;

        // Write and download the file
        XLSX.writeFile(wb, fileName);

      } catch (error) {
        console.error('Error generating Excel file:', error);
        alert('Failed to generate Excel file. Please try again.');
      }
    }
  }
};
</script>

<style src="@/assets/css/hr-page-shared.css"></style>
<style scoped>
.hr-filters-row { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 16px; }
.hr-search-wrap { flex: 1; min-width: 200px; }
.dept-cell-name { display: flex; align-items: center; gap: 12px; }
.dept-avatar { width: 40px; height: 40px; border-radius: 50%; background: rgba(91,115,232,0.15); color: #5b73e8; display: flex; align-items: center; justify-content: center; font-weight: 600; flex-shrink: 0; }
.hr-btn-group { display: flex; gap: 8px; flex-wrap: wrap; }
.hr-budget-input { display: flex; gap: 8px; align-items: stretch; }
.hr-budget-input .hr-select { flex-shrink: 0; }
.hr-budget-input .hr-input { flex: 1; min-width: 0; }
.span-half { grid-column: span 1; }
.span-full { grid-column: 1 / -1; }
.hr-text-danger { color: #ef4444; }
.hr-text-info { color: #06b6d4; }
.hr-d-block { display: block; }
.hr-mt-1 { margin-top: 4px; }
.hr-mt-3 { margin-top: 12px; }
.hr-mb-3 { margin-bottom: 12px; }
.hr-py-4 { padding-top: 16px; padding-bottom: 16px; }
.hr-p-0 { padding: 0; }
.hr-flex-between { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; }
.dept-view-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; }
.dept-view-item { margin-bottom: 12px; }
.dept-view-item:last-child { margin-bottom: 0; }
.dept-view-item label { display: block; font-size: 0.8rem; margin-bottom: 4px; }
.hr-small { font-size: 0.85rem; }
.hr-spinner.small { width: 18px; height: 18px; border-width: 2px; display: inline-block; vertical-align: middle; margin-right: 6px; }
.bx-spin { animation: spin 1s linear infinite; }
.hr-table-striped tbody tr:nth-child(even) { background: #f8fafc; }
.hr-table-hover tbody tr:hover { background: rgba(91,115,232,0.05); }
@keyframes spin { to { transform: rotate(360deg); } }
</style>